import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const AdminResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [tokenStatus, setTokenStatus] = useState('checking'); // 'checking' | 'valid' | 'invalid'
  const [tokenReason, setTokenReason] = useState(null);
  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!token) {
        setTokenStatus('invalid');
        setTokenReason('missing');
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/admin/verify-reset-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await response.json();
        if (cancelled) return;
        if (data.valid) {
          setTokenStatus('valid');
          setUsername(data.username || '');
        } else {
          setTokenStatus('invalid');
          setTokenReason(data.reason || 'invalid');
        }
      } catch {
        if (!cancelled) {
          setTokenStatus('invalid');
          setTokenReason('network');
        }
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [API_URL, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      const data = await response.json();
      if (response.ok) {
        setIsComplete(true);
        toast.success('Password reset successfully');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center px-4 py-10">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-100 flex items-center justify-center mb-3">
            <KeyRound className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">DCELC Admin</h1>
          <p className="text-sm text-slate-500 mt-1">Reset your password</p>
        </div>

        {tokenStatus === 'checking' && (
          <div className="text-center py-8 text-slate-500" data-testid="reset-token-checking">
            <p>Verifying your reset link…</p>
          </div>
        )}

        {tokenStatus === 'invalid' && (
          <div className="text-center py-6" data-testid="reset-token-invalid">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Reset link is invalid</h2>
            <p className="text-sm text-slate-500 mb-5">
              {tokenReason === 'expired'
                ? 'This reset link has expired. Reset links are valid for 1 hour.'
                : tokenReason === 'already_used'
                ? 'This reset link has already been used.'
                : tokenReason === 'missing'
                ? 'No reset token was provided in the URL.'
                : 'We could not verify this reset link.'}
              <br />
              Please request a new password reset from the admin login page.
            </p>
            <Button
              onClick={() => navigate('/admin')}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="reset-back-to-login"
            >
              Back to Login
            </Button>
          </div>
        )}

        {tokenStatus === 'valid' && isComplete && (
          <div className="text-center py-4" data-testid="reset-complete">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-4">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Password reset!</h2>
            <p className="text-sm text-slate-500 mb-5">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Button
              onClick={() => navigate('/admin')}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="reset-go-to-login"
            >
              Continue to Login
            </Button>
          </div>
        )}

        {tokenStatus === 'valid' && !isComplete && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
            {username && (
              <div className="bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-600">
                Resetting password for <span className="font-semibold text-slate-800">{username}</span>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">New password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  data-testid="reset-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Confirm new password</label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                required
                minLength={8}
                data-testid="reset-confirm-password"
              />
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white py-5"
              data-testid="reset-submit"
            >
              <Lock className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Resetting…' : 'Reset Password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminResetPassword;
